document.documentElement.classList.remove('no-js');

// Native lazy loading can fetch catalog artwork far below the first screen.
// Reserve its existing media box, then load shortly before it enters view.
function initializeDeferredMedia(root = document) {
  const templates = root.querySelectorAll('template[data-deferred-media]');
  const reveal = (template) => template.replaceWith(template.content.cloneNode(true));
  if (!('IntersectionObserver' in window)) {
    templates.forEach(reveal);
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const template = entry.target.querySelector('template[data-deferred-media]');
      if (template) reveal(template);
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '300px 0px' });
  templates.forEach((template) => observer.observe(template.parentElement));
}
initializeDeferredMedia();
document.addEventListener('shopify:section:load', (event) => initializeDeferredMedia(event.target));

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-localization-select]');
  if (select) select.form?.submit();

  const autoSubmit = event.target.closest('[data-auto-submit]');
  if (autoSubmit) autoSubmit.form?.submit();

  const variant = event.target.closest('[data-variant-selector]');
  if (variant) {
    const url = new URL(variant.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.value);
    window.location.assign(url.toString());
  }
});

document.addEventListener('input', (event) => {
  const input = event.target.closest('[data-quantity-input]');
  if (!input) return;

  const minimum = Number(input.min || 1);
  const step = Number(input.step || 1);
  const value = Number(input.value || 0);
  input.setCustomValidity('');

  if (value < minimum || (value - minimum) % step !== 0) {
    input.setCustomValidity(input.dataset.quantityError || `Use a quantity of at least ${minimum} in increments of ${step}.`);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const openMenu = document.activeElement?.closest('.site-header details[open]');
  if (!openMenu) return;

  openMenu.removeAttribute('open');
  openMenu.querySelector(':scope > summary')?.focus();
});

document.addEventListener('click', (event) => {
  const chatLink = event.target.closest('[data-open-shopify-chat]');
  if (chatLink) {
    const chatButton = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('[part="activator"]');
    if (chatButton) {
      event.preventDefault();
      chatButton.click();
      return;
    }
  }

  document.querySelectorAll('.main-nav--desktop details[open]').forEach((menu) => {
    if (!menu.contains(event.target)) menu.removeAttribute('open');
  });
});


// Compact part identifiers without joining ordinary product-description words.
function normalizeWqcSearchQuery(value) {
  const query = value.trim();
  const tokens = query.match(/[\p{L}\p{N}]+/gu) || [];
  const hasDigit = (token) => /[0-9]/.test(token);
  const identifier = tokens.length > 0 && (
    tokens.every(hasDigit) ||
    (/^[A-Za-z]{1,3}$/.test(tokens[0]) && tokens.length > 1 && tokens.slice(1).every(hasDigit)) ||
    (!/\s/.test(query) && tokens.some(hasDigit) && tokens.every((token) => hasDigit(token) || /^[A-Za-z]{1,3}$/.test(token)))
  );
  return identifier ? tokens.join('') : query;
}

// Delegation covers header, home-page and search-results forms, including
// theme-editor section replacements. Capture runs before predictive cleanup.
document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[role="search"]');
  const input = form?.querySelector('input[name="q"]');
  if (input) input.value = normalizeWqcSearchQuery(input.value);
}, true);
document.addEventListener('change', (event) => {
  const input = event.target.closest('form[role="search"] input[name="q"]');
  if (input) input.value = normalizeWqcSearchQuery(input.value);
});

// Progressive enhancement: the ordinary search form remains available on failure.
function initializeWqcPredictiveSearch(root = document) {
  root.querySelectorAll('form[data-wqc-predictive]').forEach((form) => {
    if (form.dataset.predictiveReady || !window.AbortController) return;
    const input = form.querySelector('input[name="q"]');
    const list = form.querySelector('[data-predictive-list]');
    const status = form.querySelector('[data-predictive-status]');
    if (!input || !list || !status || !form.dataset.suggestUrl) return;
    form.dataset.predictiveReady = 'true';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');
    let timer;
    let controller;
    let version = 0;
    let active = -1;
    let links = [];
    const close = () => {
      clearTimeout(timer);
      controller?.abort();
      version += 1;
      list.hidden = true;
      active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      status.textContent = '';
    };
    const activate = (index) => {
      active = index;
      links.forEach((link, i) => link.parentElement.setAttribute('aria-selected', String(i === active)));
      if (active >= 0) {
        input.setAttribute('aria-activedescendant', links[active].parentElement.id);
        links[active].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    };
    const schedule = () => {
      close();
      const query = normalizeWqcSearchQuery(input.value);
      if (query.length < 2 || input.composing) return;
      const requestVersion = version;
      timer = setTimeout(async () => {
        controller = new AbortController();
        try {
          const url = new URL(form.dataset.suggestUrl + '.json', location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '5');
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Predictive search unavailable');
          const data = await response.json();
          if (version !== requestVersion || document.activeElement !== input || normalizeWqcSearchQuery(input.value) !== query) return;
          list.replaceChildren();
          links = [];
          for (const product of data.resources?.results?.products || []) {
            const productUrl = new URL(product.url, location.origin);
            if (productUrl.origin !== location.origin) continue;
            // Preserve an explicit theme preview when following a suggestion.
            for (const key of ['preview_theme_id', '_fd', '_sc']) {
              const value = new URL(location.href).searchParams.get(key);
              if (value !== null) productUrl.searchParams.set(key, value);
            }
            const item = document.createElement('li');
            item.id = list.id + '-' + links.length;
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', 'false');
            const link = document.createElement('a');
            link.href = productUrl.href;
            link.tabIndex = -1;
            link.textContent = product.title;
            item.append(link);
            list.append(item);
            links.push(link);
          }
          list.hidden = links.length === 0;
          input.setAttribute('aria-expanded', String(links.length > 0));
          status.textContent = links.length ? form.dataset.countLabel.replace('[count]', links.length) : form.dataset.emptyLabel;
        } catch (error) {
          if (error.name !== 'AbortError' && version === requestVersion) close();
        }
      }, 250);
    };
    input.addEventListener('input', (event) => { if (!event.isComposing) schedule(); });
    input.addEventListener('compositionend', schedule);
    input.addEventListener('focus', schedule);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { close(); return; }
      if (event.key === 'Tab') { close(); return; }
      if (list.hidden || !links.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        activate(active < 0 ? (direction === 1 ? 0 : links.length - 1) : (active + direction + links.length) % links.length);
      } else if (event.key === 'Enter' && active >= 0) {
        event.preventDefault();
        location.assign(links[active].href);
      }
    });
    form.addEventListener('focusout', (event) => { if (!form.contains(event.relatedTarget)) close(); });
    document.addEventListener('click', (event) => { if (!form.contains(event.target)) close(); });
    form.addEventListener('submit', close);
  });
}
initializeWqcPredictiveSearch();
document.addEventListener('shopify:section:load', (event) => initializeWqcPredictiveSearch(event.target));
